import { isApolloError } from '@apollo/client';
import { NextPage } from 'next';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { logout, refresh } from '../../utils/auth';
import getApolloErrorType from '../../utils/getApolloErrorType';
import sentry from '../../utils/sentry';
import ErrorTemplate from '../ErrorTemplate';
import CheckoutEmpty from './CheckoutEmpty';
import NotFound from './NotFound';

const { captureException } = sentry();

interface Props {
    statusCode?: number;
    error?: Error | null;
}

const getDescription = (errorCode: string) => {
    switch (errorCode) {
        case '401':
        case '403':
            return <FormattedMessage defaultMessage="U heeft niet de juiste rechten voor deze actie" />;

        default:
            return (
                <FormattedMessage defaultMessage="Bij het opvragen van deze pagina is er iets misgegaan. Probeer het later opnieuw." />
            );
    }
};

const ErrorPage: NextPage<Props> = ({ error, ...props }) => {
    const apolloError = getApolloErrorType(error);

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const statusCode = apolloError || (props.statusCode ? String(props.statusCode) : undefined) || '500';

    useEffect(() => {
        if (error && isApolloError(error)) {
            if (getApolloErrorType(error) === '401') {
                refresh().then(success => !success && logout());
            }
            // eslint-disable-next-line no-console
            console.error(error.message);
        }
    }, [error]);

    if (error) {
        switch (error.name) {
            case 'NotFoundError':
                return <NotFound />;
            case 'InvalidCheckoutError':
                return <CheckoutEmpty />;
            default:
                break;
        }
    }

    return (
        <ErrorTemplate
            title={<FormattedMessage defaultMessage="Oeps! {statusCode}" values={{ statusCode }} />}
            description={getDescription(statusCode)}
        />
    );
};

// eslint-disable-next-line @typescript-eslint/require-await
ErrorPage.getInitialProps = async ctx => {
    const { res, err } = ctx;
    if (err) {
        captureException(err, ctx);
    }

    return { statusCode: res?.statusCode, error: err };
};

export default ErrorPage;
